import { Investor } from "@/types/investor";
import { enrichmentDebugState } from "../debugUtils";
import { parseJsonResponse } from "../parsingUtils";

/**
 * Function to enrich data using OpenAI
 */
export async function enrichWithOpenAI(
  investor: Investor,
  relevanceContext: string,
  apiKey: string
): Promise<Investor> {
  try {
    // Create a prompt for OpenAI to enrich investor data
    const prompt = `
      You are a venture capital research specialist tasked with enriching information about ${investor.name || "Unknown"} from ${investor.firm}.
      
      Based on your knowledge, provide the following information in JSON format:
      
      1. Investment Focus: What sectors, stages, check sizes, and geographies do they focus on?
      2. Portfolio Companies: List 5-10 notable companies they've invested in
      3. Keywords/Interests: Extract key investment themes
      4. Website: The official website URL of the firm if you know it
      5. Relevance Score: On a scale of 1-10, how relevant is this investor for: "${relevanceContext || "Technology startup seeking investment"}"
      
      EXTREMELY IMPORTANT: Be rigorous and accurate in your relevance scoring. The relevance score must reflect how aligned the investor is with: ${relevanceContext || "Technology startup seeking investment"}
      
      Use the full range from 1-10, where:
      1-3 = Poor fit (minimal overlap with focus areas)
      4-6 = Moderate fit (some overlap but not ideal)
      7-8 = Good fit (significant overlap with focus areas)
      9-10 = Excellent fit (perfect alignment with focus areas)
      
      Format your response as a JSON object with these fields:
      {
        "investmentFocus": "Detailed description of their investment focus",
        "portfolioCompanies": ["Company 1", "Company 2", "Company 3"],
        "keywords": ["keyword1", "keyword2", "keyword3"],
        "relevanceScore": 7.5,
        "website": "https://example.com",
        "confidence": "high/medium/low",
        "notes": "Brief summary of data quality"
      }
      
      If you don't know certain information, provide your best guess based on similar firms but indicate lower confidence.
    `;
    
    // Save the prompt for debugging
    enrichmentDebugState.lastPrompt = prompt;
    enrichmentDebugState.timestamp = new Date().toISOString();
    const startTime = Date.now();

    // Make request to OpenAI API
    const response = await fetch("https://api.openai.com/v1/chat/completions", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${apiKey}`
      },
      body: JSON.stringify({
        model: "gpt-4o",
        messages: [
          {
            role: "system",
            content: "You are a venture capital research specialist with extensive knowledge about investors and firms. Return only valid JSON as your response."
          },
          {
            role: "user",
            content: prompt
          }
        ],
        temperature: 0.3,
        max_tokens: 1000,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error?.message || "Failed to enrich investor data with OpenAI");
    }

    const data = await response.json();
    const content = data.choices[0].message.content;
    
    // Save the response and timing for debugging
    enrichmentDebugState.lastResponse = content;
    enrichmentDebugState.responseTime = Date.now() - startTime;
    enrichmentDebugState.model = data.model || "gpt-4o";
    
    // Parse the JSON response
    const enrichedData = parseJsonResponse(content);
    
    // Return investor with enriched data
    return {
      ...investor,
      investmentFocus: enrichedData.investmentFocus || investor.investmentFocus,
      portfolioCompanies: enrichedData.portfolioCompanies || investor.portfolioCompanies,
      keywords: enrichedData.keywords || investor.keywords,
      relevanceScore: enrichedData.relevanceScore || investor.relevanceScore,
      website: enrichedData.website || investor.website,
      lastRelevanceUpdate: new Date().toISOString(),
      confidence: enrichedData.confidence || "medium",
      notes: enrichedData.notes || "",
      sources: [],
      enrichedWith: "openai" // Make sure this is one of the allowed values in the updated type
    };
  } catch (error) {
    console.error("Error enriching with OpenAI:", error);
    // Return original investor if there was an error
    return investor;
  }
}
